import { motion } from 'framer-motion';
import { Download, Briefcase, GraduationCap, Award } from 'lucide-react';

const Resume = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const experience = [
    {
      title: 'Senior Full Stack Developer',
      company: 'Tech Corp',
      period: '2021 - Present',
      description: 'Leading development of enterprise applications using React, Node.js, and AWS. Managing team of 5 developers.'
    },
    {
      title: 'Technical Project Manager',
      company: 'Digital Solutions Inc',
      period: '2019 - 2021',
      description: 'Managed multiple concurrent projects with budgets up to $2M. Implemented Agile methodologies across organization.'
    },
    {
      title: 'Full Stack Developer',
      company: 'StartUp Ventures',
      period: '2017 - 2019',
      description: 'Built scalable web applications from scratch. Worked with React, Python, and PostgreSQL.'
    }
  ];

  const education = [
    {
      degree: 'Master of Science in Computer Science',
      institution: 'University of Technology',
      year: '2017'
    },
    {
      degree: 'Bachelor of Science in Software Engineering',
      institution: 'State University',
      year: '2015'
    }
  ];

  const skills = {
    'Frontend': ['React', 'Vue.js', 'TypeScript', 'Tailwind CSS', 'Next.js'],
    'Backend': ['Node.js', 'Python', 'Express', 'Django', 'PostgreSQL', 'MongoDB'],
    'Blockchain': ['Solidity', 'Web3.js', 'Ethereum', 'Smart Contracts'],
    'AI/ML': ['TensorFlow', 'PyTorch', 'NLP', 'Computer Vision'],
    'DevOps': ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'GitHub Actions'],
    'Tools': ['Git', 'Jira', 'Figma', 'Postman']
  };

  return (
    <div className="pt-32 pb-24">
      <div className="container-custom max-w-5xl">
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center mb-16"
        >
          <h1 className="mb-6">Resume</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
            Experienced developer and technical leader with a passion for building exceptional digital products.
          </p>
          <button className="btn-primary inline-flex items-center">
            <Download size={20} className="mr-2" />
            Download PDF
          </button>
        </motion.div>

        {/* Experience */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-16"
        >
          <div className="flex items-center mb-8">
            <Briefcase className="text-[rgb(var(--color-accent))] mr-3" size={28} />
            <h2>Experience</h2>
          </div>
          <div className="space-y-8">
            {experience.map((job, index) => (
              <div key={index} className="card">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                  <div>
                    <h3 className="text-xl font-semibold">{job.title}</h3>
                    <p className="text-[rgb(var(--color-accent))] font-medium">{job.company}</p>
                  </div>
                  <span className="text-gray-600 dark:text-gray-400 text-sm md:text-base mt-2 md:mt-0">{job.period}</span>
                </div>
                <p className="text-gray-600 dark:text-gray-400">{job.description}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Education */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-16"
        >
          <div className="flex items-center mb-8">
            <GraduationCap className="text-[rgb(var(--color-accent))] mr-3" size={28} />
            <h2>Education</h2>
          </div>
          <div className="space-y-6">
            {education.map((edu, index) => (
              <div key={index} className="card">
                <h3 className="text-xl font-semibold mb-1">{edu.degree}</h3>
                <p className="text-[rgb(var(--color-accent))] font-medium">{edu.institution}</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">{edu.year}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Skills */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <div className="flex items-center mb-8">
            <Award className="text-[rgb(var(--color-accent))] mr-3" size={28} />
            <h2>Skills</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(skills).map(([category, items]) => (
              <div key={category} className="card">
                <h3 className="text-lg font-semibold mb-4 text-[rgb(var(--color-accent))]">{category}</h3>
                <div className="flex flex-wrap gap-2">
                  {items.map((skill) => (
                    <span
                      key={skill}
                      className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Resume;
